import GooglePayButton from "@google-pay/button-react";
import React, {useEffect, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {useDarkMode} from "../hooks/useDarkMode";
import {TipCurrency} from "../model/constants";
import {
    CsobGooglePayInitParams,
    ShareKeyType,
    googlePayInit,
    googlePayProcess,
    loadGooglePayInitParams,
} from "../model/paymentApi";
import {getBrowserData, handlePaymentProcess} from "../model/paymentUtils";
import {Fingerprint} from "./Fingerprint";

function formatPaymentRequest(
    initParams: CsobGooglePayInitParams,
    price: string,
    currency: TipCurrency
): google.payments.api.PaymentDataRequest {
    return {
        apiVersion: initParams.apiVersion,
        apiVersionMinor: initParams.apiVersionMinor,
        allowedPaymentMethods: [
            {
                type: initParams.paymentMethodType,
                parameters: {
                    allowedAuthMethods: initParams.allowedCardAuthMethods,
                    allowedCardNetworks: initParams.allowedCardNetworks,
                },
                tokenizationSpecification: {
                    type: initParams.tokenizationSpecificationType,
                    parameters: {
                        gateway: initParams.gateway,
                        gatewayMerchantId: initParams.gatewayMerchantId,
                    },
                } as google.payments.api.PaymentMethodTokenizationSpecification,
            },
        ],
        merchantInfo: {
            merchantId: initParams.googlepayMerchantId,
            merchantName: initParams.merchantName,
        },
        transactionInfo: {
            totalPriceStatus: "FINAL",
            totalPrice: price,
            currencyCode: currency,
            countryCode: initParams.countryCode, //z payloadu
        },
    };
}

function formatReadyToPay(
    initParams: CsobGooglePayInitParams
): google.payments.api.IsReadyToPayRequest {
    return {
        apiVersion: initParams.apiVersion,
        apiVersionMinor: initParams.apiVersion,
        allowedPaymentMethods: [
            {
                type: initParams.paymentMethodType,
                parameters: {
                    allowedAuthMethods: initParams.allowedCardAuthMethods,
                    allowedCardNetworks: initParams.allowedCardNetworks,
                },
                tokenizationSpecification: {
                    type: initParams.tokenizationSpecificationType,
                    parameters: {
                        gateway: initParams.gateway,
                        gatewayMerchantId: initParams.gatewayMerchantId,
                    },
                } as google.payments.api.PaymentMethodTokenizationSpecification,
            },
        ],
    };
}

export function GooglePay({
    price,
    currency,
    setShowGooglePay,
    shareKey,
    shareKeyType,
}: {
    price: string;
    currency: TipCurrency;
    setShowGooglePay: (value: boolean) => void;
    shareKey: string;
    shareKeyType: ShareKeyType;
}) {
    const [paymentParams, setPaymentParams] = useState<CsobGooglePayInitParams | null>(null);
    const fingerPrintRef = useRef(null);
    const isDark = useDarkMode();
    const intl = useIntl();

    useEffect(() => {
        const loadPayment = async () => {
            try {
                const {initParams} = await loadGooglePayInitParams();
                setPaymentParams(initParams);
                const paymentsClient = new google.payments.api.PaymentsClient({
                    environment: initParams.environment,
                });
                const {result} = await paymentsClient.isReadyToPay(formatReadyToPay(initParams));
                if (!result) {
                    setShowGooglePay(false);
                }
            } catch (error) {
                setShowGooglePay(false);
                console.error(error);
            }
        };
        loadPayment();
    }, [setShowGooglePay]);

    const handleLoad = async (paymentRequest: google.payments.api.PaymentData) => {
        const paymentToken = paymentRequest.paymentMethodData.tokenizationData.token;
        try {
            const initResponse = await googlePayInit({
                shareKey: shareKey,
                shareKeyType: shareKeyType,
                redirectUri: window.location.href,
                price: {amount: parseInt(price), ccy: currency},
                paymentToken: btoa(paymentToken),
            });

            if (initResponse.fingerPrintUri && fingerPrintRef.current) {
                const fingerPrintIframe: HTMLIFrameElement = fingerPrintRef.current;
                fingerPrintIframe.src = initResponse.fingerPrintUri;
            }
            if (initResponse.trackingTipId) {
                const processResponse = await googlePayProcess({
                    trackingTipId: initResponse.trackingTipId,
                    browserJsData: getBrowserData(),
                });
                handlePaymentProcess(processResponse);
            } else {
                console.error("Payment process failed");
            }
        } catch (error) {
            alert(intl.formatMessage({id: "generic_error"}));
            console.error(error);
        }
    };

    return (
        <>
            {paymentParams && (
                <GooglePayButton
                    data-cy="tracking-tip-google-pay-btn"
                    environment={paymentParams.environment}
                    paymentRequest={formatPaymentRequest(paymentParams, price, currency)}
                    onLoadPaymentData={handleLoad}
                    onError={(error) => console.error(error)}
                    buttonColor={isDark ? "white" : "black"}
                    buttonSizeMode="fill"
                    buttonType="pay"
                />
            )}
            <Fingerprint ref={fingerPrintRef} />
        </>
    );
}
